import React from 'react';
import PropTypes from 'prop-types';
import { Link, Icon } from '@resy/ui-kitchen';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { ACQUISITION_CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import ExternalLink from '../../../../../../ui-kitchen/src/svg/icons/external-link.svg';
import './AmexResyCreditMGM.scss';

const AmexResyCreditMGM = ({
  awsFolderName,
  baseRequestUrl,
  sectionConfig,
  eventAnalytics,
}) => {
  const sectionId = sectionConfig?.section_id || sectionConfig?.id;
  const { eyebrow, title, fine_print: finePrint } = sectionConfig || {};
  const description = sectionConfig?.copy || '';
  const cards = sectionConfig?.cards || [];
  const ctaLabel = sectionConfig?.cta?.label || 'Refer a Friend';
  const ctaUrl = sectionConfig?.cta?.url || '#';

  const imagePrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const sanitizedEyebrow = sanitizeString(eyebrow);
  const sanitizedTitle = sanitizeString(title);
  const sanitizedDescription = sanitizeString(description);
  const sanitizedFinePrint = sanitizeString(finePrint);

  const showMgmCards = () => cards.map((card) => {
    const srcRegular = `${imagePrefix}${card.name_regular}`;
    const srcRetina = `${imagePrefix}${card.name_retina}`;

    return (
      <div
        key={`${card.name_regular}-${card.alt}`}
        className="AmexResyCreditMGM__cards-tile"
      >
        <img
          className="AmexResyCreditMGM__cards-picture"
          src={srcRegular}
          srcSet={`${srcRegular} 1x, ${srcRetina} 2x`}
          alt={card.alt || ''}
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  });

  return (
    <section className="AmexResyCreditMGM" aria-labelledby={sectionId}>
      <div className="container">
        <div className="AmexResyCreditMGM__content">
          {eyebrow && (
            <h2
              id={sectionId}
              className="AmexResyCreditMGM__eyebrow"
              dangerouslySetInnerHTML={{ __html: sanitizedEyebrow }} // eslint-disable-line react/no-danger
            />
          )}
          {title && (
            <h3
              className="AmexResyCreditMGM__title"
              dangerouslySetInnerHTML={{ __html: sanitizedTitle }} // eslint-disable-line react/no-danger
            />
          )}
          {description && (
            <p
              className="AmexResyCreditMGM__copy"
              dangerouslySetInnerHTML={{ __html: sanitizedDescription }} // eslint-disable-line react/no-danger
            />
          )}
          {finePrint && (
          <p
            className="AmexResyCreditMGM__fine-print"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedFinePrint,
            }}
          />
          )}
          {cards.length > 0 && (
            <div className="AmexResyCreditMGM__cards-grid">
              {showMgmCards()}
            </div>
          )}
          {ctaLabel && ctaUrl && (
            <Link
              className="AmexResyCreditMGM__cta"
              href={ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                handleMktAnalytics(eventAnalytics, ACQUISITION_CTA_EVENT_NAME, {
                  action_name: 'MGM - Click',
                });
              }}
            >
              {ctaLabel}
              <Icon component={ExternalLink} role="presentation" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

AmexResyCreditMGM.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  sectionConfig: PropTypes.shape({
    section_id: PropTypes.string,
    id: PropTypes.string,
    eyebrow: PropTypes.string,
    title: PropTypes.string,
    copy: PropTypes.string,
    cards: PropTypes.arrayOf(PropTypes.shape({
      name_regular: PropTypes.string,
      name_retina: PropTypes.string,
      alt: PropTypes.string,
    })),
    cta: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  }).isRequired,
  eventAnalytics: PropTypes.func,
};

AmexResyCreditMGM.defaultProps = {
  eventAnalytics: null,
};

export default AmexResyCreditMGM;
